import { CalendarClock, CalendarX2, CheckCircle2, CircleDot, Clock, XCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { BookingView } from "@/lib/booking-view";
import type { DoctorStatus } from "@/types/doctor";
import { cn } from "@/lib/utils";

const TONES = {
  neutral: "bg-muted text-muted-foreground border-border",
  success: "bg-success/10 text-success border-success/25",
  warning: "bg-warning/12 text-warning border-warning/25",
  danger: "bg-destructive/10 text-destructive border-destructive/25",
  info: "bg-info/10 text-info border-info/25",
  brand: "bg-primary/10 text-primary border-primary/20",
} as const;

export type BadgeTone = keyof typeof TONES;

export function StatusBadge({
  label,
  tone = "neutral",
  icon: Icon,
  className,
}: {
  label: string;
  tone?: BadgeTone;
  icon?: LucideIcon | undefined;
  className?: string | undefined;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tracking-wide capitalize",
        TONES[tone],
        className,
      )}
    >
      {Icon ? (
        <Icon className="size-3.5" aria-hidden="true" />
      ) : (
        <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

/** One status vocabulary shared by the Doctor Portal and the Admin Portal. */
export const DOCTOR_STATUS_META: Record<
  DoctorStatus,
  { tone: BadgeTone; icon: LucideIcon; label: string }
> = {
  pending: { tone: "warning", icon: Clock, label: "Pending" },
  approved: { tone: "success", icon: CheckCircle2, label: "Approved" },
  rejected: { tone: "danger", icon: XCircle, label: "Rejected" },
};

export function DoctorStatusBadge({
  status,
  className,
}: {
  status: DoctorStatus;
  className?: string | undefined;
}) {
  const meta = DOCTOR_STATUS_META[status];
  return <StatusBadge label={meta.label} tone={meta.tone} icon={meta.icon} className={className} />;
}

/**
 * The portal's four buckets, which now map 1:1 onto the backend's four booking
 * statuses. See `src/lib/booking-view.ts`.
 */
export const BOOKING_VIEW_META: Record<
  BookingView,
  { tone: BadgeTone; icon: LucideIcon; label: string }
> = {
  booked: { tone: "info", icon: CalendarClock, label: "Booked" },
  completed: { tone: "success", icon: CheckCircle2, label: "Completed" },
  missed: { tone: "warning", icon: CalendarX2, label: "Missed" },
  cancelled: { tone: "danger", icon: CircleDot, label: "Cancelled" },
};

export function BookingStatusBadge({
  status,
  className,
}: {
  status: BookingView;
  className?: string | undefined;
}) {
  const meta = BOOKING_VIEW_META[status];
  return <StatusBadge label={meta.label} tone={meta.tone} icon={meta.icon} className={className} />;
}
